<template>
    <div class="w-full">
        <DataTable :value="list" responsiveLayout="scroll">
            <Column field="name" sortable>
                <template #header>
                    <span class="text-sm text-600 font-medium">Мерчант</span>
                </template>
                <template #body="{ data }">
                    <div class="flex flex-column">
                        <span class="font-medium">{{ data.name }}</span>
                        <span class="text-600 text-xs">{{ shortId(data.id) }}</span>
                    </div>
                </template>
            </Column>

            <Column field="active">
                <template #header>
                    <span class="text-sm text-600 font-medium">Статус</span>
                </template>
                <template #body="{ data }">
                    <Tag :severity="data.active ? 'success' : 'warning'" :value="data.active ? 'Активен' : 'Не активен'" />
                </template>
            </Column>

            <Column field="deposit" sortable>
                <template #header>
                    <span class="text-sm text-600 font-medium">Депозит</span>
                </template>
                <template #body="{ data }">
                    {{ formatAmount(data.deposit, 'usdt') }}
                </template>
                <template #footer>
                    <span class="text-sm text-600 font-medium">Итого: {{ totalDeposit }}</span>
                </template>
            </Column>

            <Column field="active_from">
                <template #header>
                    <span class="text-sm text-600 font-medium">Время работы</span>
                </template>
                <template #body="{ data }">
                    <div class="flex flex-column">
                        <span class="text-sm text-700 white-space-nowrap">
                            {{ getMskTime(data.active_from) }} - {{ getMskTime(data.active_to) }} (МСК)
                        </span>
                        <span class="text-xs text-600 white-space-nowrap">
                            {{ getLocalTime(data.active_from) }} - {{ getLocalTime(data.active_to) }} (Bali)
                        </span>
                    </div>
                </template>
            </Column>

            <Column>
                <template #header>
                    <span class="text-sm text-600 font-medium">Валюты приёма</span>
                </template>
                <template #body="{ data }">
                    <div class="block text-left white-space-normal overflow-visible line-height-3 py-1 text-600 cursor-pointer"
                        :style="{ wordBreak: 'break-word' }" role="button" tabindex="0"
                        @click="toggleOverlay($event, data.id, 'get-currencies')"
                        @keydown.enter.prevent="toggleOverlay($event, data.id, 'get-currencies')">
                        <i v-if="getCodes(data.get_currency_codes).length" class="pi pi-chevron-down mr-1 text-xs" />
                        {{ previewCurrencies(data.get_currency_codes) }}
                    </div>
                    <OverlayPanel :ref="overlayRef(data.id, 'get-currencies')">
                        <div class="flex flex-wrap gap-1 max-w-20rem">
                            <Tag v-for="code in getCodes(data.get_currency_codes)" :key="'gc-' + data.id + '-' + code"
                                :value="String(code).toUpperCase()" severity="secondary" class="text-600" />
                            <span v-if="!getCodes(data.get_currency_codes).length" class="text-600 text-xs">Нет значений</span>
                        </div>
                    </OverlayPanel>
                </template>
            </Column>

            <Column>
                <template #header>
                    <span class="text-sm text-600 font-medium">Валюты выплаты</span>
                </template>
                <template #body="{ data }">
                    <div class="block text-left white-space-normal overflow-visible line-height-3 py-1 text-600 cursor-pointer"
                        :style="{ wordBreak: 'break-word' }" role="button" tabindex="0"
                        @click="toggleOverlay($event, data.id, 'send-currencies')"
                        @keydown.enter.prevent="toggleOverlay($event, data.id, 'send-currencies')">
                        <i v-if="getCodes(data.send_currency_codes).length" class="pi pi-chevron-down mr-1 text-xs" />
                        {{ previewCurrencies(data.send_currency_codes) }}
                    </div>
                    <OverlayPanel :ref="overlayRef(data.id, 'send-currencies')">
                        <div class="flex flex-wrap gap-1 max-w-20rem">
                            <Tag v-for="code in getCodes(data.send_currency_codes)" :key="'sc-' + data.id + '-' + code"
                                :value="String(code).toUpperCase()" severity="secondary" class="text-600" />
                            <span v-if="!getCodes(data.send_currency_codes).length" class="text-600 text-xs">Нет значений</span>
                        </div>
                    </OverlayPanel>
                </template>
            </Column>

            <Column>
                <template #header>
                    <span class="text-sm text-600 font-medium">Банки приёма</span>
                </template>
                <template #body="{ data }">
                    <div class="block text-left white-space-normal overflow-visible line-height-3 py-1 text-600 cursor-pointer"
                        :style="{ wordBreak: 'break-word' }" role="button" tabindex="0"
                        @click="toggleOverlay($event, data.id, 'get-banks')"
                        @keydown.enter.prevent="toggleOverlay($event, data.id, 'get-banks')">
                        <i v-if="bankItems(data, true).length" class="pi pi-chevron-down mr-1 text-xs" />
                        {{ previewBanks(data, true) }}
                    </div>
                    <OverlayPanel :ref="overlayRef(data.id, 'get-banks')">
                        <div class="flex flex-column gap-1 max-w-20rem">
                            <span v-for="item in bankItems(data, true)" :key="'gb-' + data.id + '-' + item.type + '-' + item.code"
                                class="inline-flex align-items-center gap-2">
                                <img v-if="item.type === 'bank' && getBankImage(item.code)" :src="getBankImage(item.code)" class="w-1rem h-1rem" alt="" />
                                <img v-if="item.type === 'pay' && getPaySysImage(item.code)" :src="getPaySysImage(item.code)" class="w-1rem h-1rem" alt="" />
                                <span class="text-sm">{{ getItemTitle(item) }}</span>
                            </span>
                            <span v-if="!bankItems(data, true).length" class="text-600 text-xs">Нет значений</span>
                        </div>
                    </OverlayPanel>
                </template>
            </Column>

            <Column>
                <template #header>
                    <span class="text-sm text-600 font-medium">Банки выплаты</span>
                </template>
                <template #body="{ data }">
                    <div class="block text-left white-space-normal overflow-visible line-height-3 py-1 text-600 cursor-pointer"
                        :style="{ wordBreak: 'break-word' }" role="button" tabindex="0"
                        @click="toggleOverlay($event, data.id, 'send-banks')"
                        @keydown.enter.prevent="toggleOverlay($event, data.id, 'send-banks')">
                        <i v-if="bankItems(data, false).length" class="pi pi-chevron-down mr-1 text-xs" />
                        {{ previewBanks(data, false) }}
                    </div>
                    <OverlayPanel :ref="overlayRef(data.id, 'send-banks')">
                        <div class="flex flex-column gap-1 max-w-20rem">
                            <span v-for="item in bankItems(data, false)" :key="'sb-' + data.id + '-' + item.type + '-' + item.code"
                                class="inline-flex align-items-center gap-2">
                                <img v-if="item.type === 'bank' && getBankImage(item.code)" :src="getBankImage(item.code)" class="w-1rem h-1rem" alt="" />
                                <img v-if="item.type === 'pay' && getPaySysImage(item.code)" :src="getPaySysImage(item.code)" class="w-1rem h-1rem" alt="" />
                                <span class="text-sm">{{ getItemTitle(item) }}</span>
                            </span>
                            <span v-if="!bankItems(data, false).length" class="text-600 text-xs">Нет значений</span>
                        </div>
                    </OverlayPanel>
                </template>
            </Column>

            <template #footer>
                <div class="flex justify-content-start p-2">
                    <span class="text-sm text-600 font-medium mr-3">Итого: {{ list ? list.length : 0 }}</span>
                </div>
            </template>
        </DataTable>
    </div>
</template>

<script>
import Helper from '@/assets/js/Helper'
import { PaymentSystemList, BankList } from '@/assets/js/classes/PaymentSystem.js'
import BigNumber from 'bignumber.js'

export default {
    name: 'MerchantTable',
    props: ['list'],
    data() {
        return {
            pays: [],
            banks: []
        }
    },
    computed: {
        lang_() {
            return this.$i18n.locale ? this.$i18n.locale : 'ru'
        }, 
        totalDeposit() { 
            let sum = new BigNumber(0)
            const arr = Array.isArray(this.list) ? this.list : []
            for (const m of arr) {
                const dep = m && m.deposit ? new BigNumber(m.deposit) : new BigNumber(0)
                sum = sum.plus(dep)
            } 
            return this.helper.format_amount(sum, 'usdt')
        }
    },
    async mounted() {
        await this.loadRefs()
    },
    methods: {
        shortId(value) {
            return this.helper.get_short_id(value)
        },
        formatAmount(value, currency) {
            return this.helper.format_amount(value, currency)
        },
        getMskTime(value){
            let time = Number(value) + 3
            if(time > 24) time = time - 24
            return time + ':00'
        },
        getLocalTime(value){
            let time = Number(value) + 8
            if(time > 24) time = time - 24
            return time + ':00'
        },
        getCodes(val) {
            if (!val || !Array.isArray(val)) return []
            return val.filter(Boolean)
        },
        previewCurrencies(codes) {
            const arr = this.getCodes(codes)
            if (!arr.length) return 'Нет значений'
            const first = arr.slice(0, 1).map((v) => String(v).toUpperCase()).join(', ')
            return arr.length > 1 ? `${first} +${arr.length - 1}` : first
        },
        bankItems(data, isGet) {
            const banks = this.getCodes(isGet ? data.get_bank_codes : data.send_bank_codes).map((code) => ({ type: 'bank', code }))
            const pays = this.getCodes(isGet ? data.pays_get_codes : data.pays_send_codes).map((code) => ({ type: 'pay', code }))
            return [...banks, ...pays]
        },
        previewBanks(data, isGet) {
            const arr = this.bankItems(data, isGet)
            if (!arr.length) return 'Нет значений'
            const first = arr.slice(0, 1).map((it) => this.getItemTitle(it)).join(', ')
            return arr.length > 1 ? `${first} +${arr.length - 1}` : first
        },
        getItemTitle(item) {
            if (item.type === 'pay') return this.getPaySysTitle(item.code)
            return this.getBankTitle(item.code)
        },
        overlayRef(id, type) {
            return `overlay_${id}_${type}`
        },
        toggleOverlay(event, id, type) {
            const refName = this.overlayRef(id, type)
            const panel = this.$refs[refName]
            const inst = Array.isArray(panel) ? panel[0] : panel
            if (inst) inst.toggle(event)
        },
        async loadRefs() {
            try {
                this.pays = await PaymentSystemList.GetList(this.$store).then((r) => r)
                if (!this.pays || !this.pays.length) this.pays = []
            } catch {
                this.pays = []
            }
            try {
                this.banks = await BankList.GetList(this.$store).then((r) => r)
                if (!this.banks || !this.banks.length) this.banks = []
            } catch {
                this.banks = []
            }
        },
        getBankTitle(code) {
            const bankObj = new BankList()
            bankObj.items = this.banks
            const t = bankObj.GetTitle(code, this.lang_)
            return t || code
        },
        getBankImage(code) {
            const bankObj = new BankList()
            bankObj.items = this.banks
            return bankObj.GetImage(code) || null
        },
        getPaySysTitle(code) {
            const paySysObj = new PaymentSystemList()
            paySysObj.items = this.pays
            const t = paySysObj.GetTitle(code, this.lang_)
            return t || code
        },
        getPaySysImage(code) {
            const paySysObj = new PaymentSystemList()
            paySysObj.items = this.pays
            return paySysObj.GetImage(code) || null
        }
    }, 
    async beforeCreate() {
        this.helper = new Helper()
    }
}
</script>
